import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router";
import { Outlet } from "react-router-dom";
import { EnvVarsProvider } from "src/contexts/envvars-context";
import { GlucoseProvider } from "src/contexts/glucose-context";

import { Layout as DashboardLayout } from "src/layouts/dashboard";
import PaymentFailed from "src/pages/paymentfailed";
import PaymentSuccessful from "src/pages/paymentsuccessful";
import WaitPage from "src/pages/wait";
import { WorkingMethodPage } from "src/pages/workmethod";

const IndexPage = lazy(() => import("src/pages/dashboard/index"));

// Academy
const AcademyDashboardPage = lazy(
  () => import("src/pages/dashboard/academy/dashboard")
);
const AcademyCoursePage = lazy(
  () => import("src/pages/dashboard/academy/course")
);

// Optimization
const Optimization = lazy(
  () => import("src/pages/dashboard/optimization/dashboard")
);
const ChatGPT = lazy(() => import("src/pages/dashboard/optimization/chatGPT"));
const MachineLearning = lazy(
  () => import("src/pages/dashboard/optimization/machinelearning")
);
const AutoTune = lazy(
  () => import("src/pages/dashboard/optimization/autotune")
);

// Blog
const BlogPostListPage = lazy(() => import("src/pages/dashboard/blog/list"));
const BlogPostDetailPage = lazy(
  () => import("src/pages/dashboard/blog/detail")
);
const BlogPostCreatePage = lazy(
  () => import("src/pages/dashboard/blog/create")
);

// Reminders
const Basals = lazy(() => import("src/pages/dashboard/reminders/basals"));
const ISF = lazy(() => import("src/pages/dashboard/reminders/isf"));
const CR = lazy(() => import("src/pages/dashboard/reminders/cr"));

// User
const UserListPage = lazy(() => import("src/pages/dashboard/users/list"));
const UserDetailPage = lazy(() => import("src/pages/dashboard/users/detail"));
const UserEditPage = lazy(() => import("src/pages/dashboard/users/edit"));

// Invoice
// const InvoiceListPage = lazy(() => import("src/pages/dashboard/invoices/list"));
// const InvoiceDetailPage = lazy(
//   () => import("src/pages/dashboard/invoices/detail")
// );

// Job
// const JobBrowsePage = lazy(() => import("src/pages/dashboard/jobs/browse"));
// const JobCreatePage = lazy(() => import("src/pages/dashboard/jobs/create"));
// const CompanyDetailPage = lazy(
//   () => import("src/pages/dashboard/jobs/companies/detail")
// );

// Logistics
// const LogisticsDashboardPage = lazy(
//   () => import("src/pages/dashboard/logistics/dashboard")
// );
// const LogisticsFleetPage = lazy(
//   () => import("src/pages/dashboard/logistics/fleet")
// );

// Order
// const OrderListPage = lazy(() => import("src/pages/dashboard/orders/list"));
// const OrderDetailPage = lazy(() => import("src/pages/dashboard/orders/detail"));

// Product
const SoftwareListPage = lazy(
  () => import("src/pages/dashboard/software/list")
);
const SoftwareCreatePage = lazy(
  () => import("src/pages/dashboard/software/create")
);

// Social
const SocialFeedPage = lazy(() => import("src/pages/dashboard/social/feed"));
const SocialProfilePage = lazy(
  () => import("src/pages/dashboard/social/profile")
);

// Other
const AccountPage = lazy(() => import("src/pages/dashboard/account"));
const AnalyticsPage = lazy(() => import("src/pages/dashboard/analytics"));
const Profiles = lazy(() => import("src/pages/dashboard/profiles"));
const BlankPage = lazy(() => import("src/pages/dashboard/blank"));
// const CalendarPage = lazy(() => import("src/pages/dashboard/calendar"));
const ChatPage = lazy(() => import("src/pages/dashboard/chat"));
const CryptoPage = lazy(() => import("src/pages/dashboard/crypto"));

const FileManagerPage = lazy(() => import("src/pages/dashboard/file-manager"));
const KanbanPage = lazy(() => import("src/pages/dashboard/kanban"));
const MailPage = lazy(() => import("src/pages/dashboard/mail"));
// const Monitoring = lazy(
//   () => import("src/pages/dashboard/datavisualisation/monitoring")
// );
const ColumnChart = lazy(
  () => import("src/pages/dashboard/datavisualisation/columnchart")
);
const HeatMap = lazy(
  () => import("src/pages/dashboard/datavisualisation/heatmappage")
);
const PieChart = lazy(
  () => import("src/pages/dashboard/datavisualisation/piepage")
);
const TimeSeries = lazy(
  () => import("src/pages/dashboard/datavisualisation/timeserieschart")
);
const PercentileChart = lazy(
  () => import("src/pages/dashboard/datavisualisation/percentilespage")
);
const GlucoseMovingAveragePage = lazy(
  () => import("src/pages/dashboard/datavisualisation/glucosemovingaveragepage")
);
const AiAssistant = lazy(() => import("src/pages/dashboard/aiassistant"));
const SettingsPage = lazy(() => import("src/pages/dashboard/settings"));
const MessagingPage = lazy(() => import("src/pages/dashboard/messaging"));
const ApkBuildPage = lazy(
  () => import("src/pages/dashboard/builder/apk-build")
);
const TrioBuildPage = lazy(() => import("src/pages/dashboard/builder/trio"));
const ILoopBuildPage = lazy(() => import("src/pages/dashboard/builder/iloop"));
const ApkBuildFiles = lazy(() => import("src/pages/dashboard/builder/files"));
const FinancePage = lazy(() => import("src/pages/dashboard/finance"));

// Cloud Services
const LibreLinkPage = lazy(
  () => import("src/pages/dashboard/cloud-services/librelink/librelink")
);
const McpNightscoutPage = lazy(
  () => import("src/pages/dashboard/cloud-services/mcp-nightscout/mcpnightscout")
);

// Nightscout Transfer
const NightscoutTransferPage = lazy(
  () => import("src/pages/dashboard/nightscout-transfer")
);

// Basal Testing Calculator
const BasalTestingCalculatorPage = lazy(
  () => import("src/pages/basal-testing-calculator")
);

// Publications
const PublicationsPage = lazy(() => import("src/pages/publications"));

export const dashboardRoutes: RouteObject[] = [
  {
    path: "dashboard",
    element: (
      <DashboardLayout>
        <Suspense>
          <EnvVarsProvider>
            <GlucoseProvider>
              <Outlet />
            </GlucoseProvider>
          </EnvVarsProvider>
        </Suspense>
      </DashboardLayout>
    ),
    children: [
      {
        index: true,
        element: <IndexPage />,
      },
      // {
      //   path: "academy",
      //   children: [
      //     {
      //       index: true,
      //       element: <AcademyDashboardPage />,
      //     },
      //     {
      //       path: "courses",
      //       children: [
      //         {
      //           path: ":courseId",
      //           element: <AcademyCoursePage />,
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   path: "optimization",
      //   children: [
      //     {
      //       index: true,
      //       element: <AcademyDashboardPage />,
      //     },
      //     {
      //       path: "chatGPT",
      //       element: <ChatGPT />,
      //     },
      //     {
      //       path: "machinelearning",
      //       element: <MachineLearning />,
      //     },
      //     {
      //       path: "autotune",
      //       element: <AutoTune />,
      //     },
      //   ],
      // },
      {
        path: "blog",
        children: [
          {
            index: true,
            element: <BlogPostListPage />,
          },
          {
            path: "create",
            element: <BlogPostCreatePage />,
          },
          {
            path: ":postId",
            element: <BlogPostDetailPage />,
          },
        ],
      },
      {
        path: "reminders",
        children: [
          // {
          //   index: true,
          //   element: <BlogPostListPage />,
          // },
          {
            path: "basals",
            element: <Basals />,
          },
          {
            path: "cr",
            element: <ISF />,
          },
          {
            path: "isf",
            element: <CR />,
          },
        ],
      },
      {
        path: "users",
        children: [
          {
            index: true,
            element: <UserListPage />,
          },
          {
            path: ":userId",
            element: <UserDetailPage />,
          },
          {
            path: ":userId/edit",
            element: <UserEditPage />,
          },
        ],
      },
      {
        path: "payment-successful",
        element: <PaymentSuccessful />,
      },
      {
        path: "payment-failed",
        element: <PaymentFailed />,
      },
      {
        path: "wait",
        element: <WaitPage />,
      },
      // {
      //   path: "invoices",
      //   children: [
      //     {
      //       index: true,
      //       element: <InvoiceListPage />,
      //     },
      //     {
      //       path: ":invoiceId",
      //       element: <InvoiceDetailPage />,
      //     },
      //   ],
      // },
      // {
      //   path: "jobs",
      //   children: [
      //     {
      //       index: true,
      //       element: <JobBrowsePage />,
      //     },
      //     {
      //       path: "create",
      //       element: <JobCreatePage />,
      //     },
      //     {
      //       path: "companies",
      //       children: [
      //         {
      //           path: ":companyId",
      //           element: <CompanyDetailPage />,
      //         },
      //       ],
      //     },
      //   ],
      // },
      // {
      //   path: "logistics",
      //   children: [
      //     {
      //       index: true,
      //       element: <LogisticsDashboardPage />,
      //     },
      //     {
      //       path: "fleet",
      //       element: <LogisticsFleetPage />,
      //     },
      //   ],
      // },
      // {
      //   path: "orders",
      //   children: [
      //     {
      //       index: true,
      //       element: <OrderListPage />,
      //     },
      //     {
      //       path: ":orderId",
      //       element: <OrderDetailPage />,
      //     },
      //   ],
      // },
      {
        path: "software",
        children: [
          {
            index: true,
            element: <SoftwareListPage />,
          },
          {
            path: "create",
            element: <SoftwareCreatePage />,
          },
        ],
      },
      {
        path: "social",
        children: [
          {
            path: "feed",
            element: <SocialFeedPage />,
          },
          {
            path: "profile",
            element: <SocialProfilePage />,
          },
        ],
      },
      {
        path: "account",
        element: <AccountPage />,
      },
      {
        path: "settings",
        element: <SettingsPage />,
      },
      {
        path: "messaging",
        element: <MessagingPage />,
      },
      {
        path: "analytics",
        element: <AnalyticsPage />,
      },
      // {
      //   path: "blank",
      //   element: <BlankPage />,
      // },
      // {
      //   path: "calendar",
      //   element: <CalendarPage />,
      // },
      // {
      //   path: "chat",
      //   element: <ChatPage />,
      // },
      // {
      //   path: "crypto",
      //   element: <CryptoPage />,
      // },
      // {
      //   path: "monitoring",
      //   element: <Monitoring />,
      // },
      {
        path: "heatmapchart",
        element: <HeatMap />,
      },
      {
        path: "Piechart",
        element: <PieChart />,
      },
      {
        path: "timeseries",
        element: <TimeSeries />,
      },
      {
        path: "percentilechart",
        element: <PercentileChart />,
      },
      {
        path: "glucosemovingaverage",
        element: <GlucoseMovingAveragePage />,
      },
      {
        path: "columnchart",
        element: <ColumnChart />,
      },
      {
        path: "optimization",
        element: <Optimization />,
      },
      {
        path: "profiles",
        element: <Profiles />,
      },
      {
        path: "file-manager",
        element: <FileManagerPage />,
      },
      {
        path: "kanban",
        element: <KanbanPage />,
      },
      {
        path: "mail",
        element: <MailPage />,
      },
      {
        path: "aiassistant",
        element: <AiAssistant />,
      },
      {
        path: "apkbuild",
        children: [
          {
            index: true,
            element: <ApkBuildPage />,
          },
          {
            path: "trio",
            element: <TrioBuildPage />,
          },
          {
            path: "iloop",
            element: <ILoopBuildPage />,
          },
          {
            path: "files",
            element: <ApkBuildFiles />,
          },
        ],
      },
      {
        path: "finance",
        element: <FinancePage />,
      },
      {
        path: "cloud-services/libre-link",
        element: <LibreLinkPage />,
      },
      {
        path: "cloud-services/mcp-nightscout",
        element: <McpNightscoutPage />,
      },
      {
        path: "basal-testing-calculator",
        element: <BasalTestingCalculatorPage />,
      },
      {
        path: "publications",
        element: <PublicationsPage />,
      },
      {
        path: "workmethod",
        element: <WorkingMethodPage />,
      },
      {
        path: "nightscout-transfer",
        element: <NightscoutTransferPage />,
      },
      // {
      //   path: "delete-nightscout-data",
      //   element: <DeleteNightscoutDataPage />,
      // },
    ],
  },
];
